import buildAudioMuteIconUrl from 'components/RedesignJourney/AudioControls/urls'
import { trackError } from 'lib/errorTracker'

const IMAGE_CACHE: Record<string, Promise<string> | undefined> = {}

export type JourneyAssetName =
  | 'Animation-v6'
  | 'For-the-Control-v6'
  | 'For-the-Eye-v5'
  | 'For-the-Feeling-v6'
  | 'For-the-Flow-v7'
  | 'For-the-Pure-v6'
  | 'For-the-Ride-v5'
  | 'For-the-Taste-v6'
  | 'Icon-v5'
  | 'Pattern-v5'

export const audioFormatPreferenceOrder = [
  { mimeType: 'audio/aac', fileType: 'aac' },
  { mimeType: 'audio/ogg', fileType: 'ogg' },
  { mimeType: 'audio/mpeg', fileType: 'mp3' },
  { mimeType: 'audio/wav', fileType: 'wav' },
]

export const preloadImage = (src: string): Promise<string> => {
  if (IMAGE_CACHE[src]) return IMAGE_CACHE[src]

  const image = new Promise<string>((resolve, reject) => {
    if (IMAGE_CACHE[src]) {
      resolve(src)
      return
    }

    const img = new Image()
    const cleanupImage = () => {
      img.onerror = null
      img.onload = null
      img.src = ''
      try {
        // @ts-expect-error -- The operand of a 'delete' operator must be optional.
        delete img.src
      } catch {
        // nothing
      }
    }
    img.src = src
    img.onload = () => {
      if ('decode' in img) {
        img.decode().then(() => {
          cleanupImage()
          resolve(src)
        })
        return
      }

      cleanupImage()
      resolve(src)
    }

    img.onerror = (event) => {
      trackError(event)
      cleanupImage()
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete IMAGE_CACHE[src]
      reject(event)
    }
  })

  IMAGE_CACHE[src] = image
  return image
}

export const imagePreloader = async (
  imageSrcs: string[],
  onProgress?: (progress: number) => void
): Promise<void> => {
  let completed = 0
  const images = imageSrcs.map((src) =>
    preloadImage(src)
      .catch(() => {})
      .finally(() => {
        if (onProgress) {
          const progress = (++completed / imageSrcs.length) * 100
          onProgress(Number(progress.toFixed(2)))
        }
      })
  )

  await Promise.all(images)
}

export const getSrcSetAttribute = (srcSet?: AssetSrcSet) => {
  if (!srcSet) {
    return undefined
  }

  const sizes = []
  for (const dpr in srcSet) {
    sizes.push(`${srcSet[dpr]} ${dpr}x`)
  }
  return sizes.join(', ')
}

export const LOADING_ASSET_URL =
  'https://assets.okapa.com/image/upload/v1562877544/bgLoading-v2.gif'

export const AUDIO_CONTROL_ASSET_URLS = (isMobile: boolean) => [
  buildAudioMuteIconUrl({ isMuted: true, isMobile, isLight: false }),
  buildAudioMuteIconUrl({ isMuted: true, isMobile, isLight: true }),
  buildAudioMuteIconUrl({ isMuted: false, isMobile, isLight: false }),
  buildAudioMuteIconUrl({ isMuted: false, isMobile, isLight: true }),
]

export type AssetType = 'image' | 'video' | 'audio'
export type DisplayAssetType = 'image' | 'video'

export type AssetSrcSet = Record<number, string>

export interface BaseDisplayAssetItem {
  src: string
  alt: string
  width: number
  height: number
}

export interface VideoAssetItem extends BaseDisplayAssetItem {
  type: 'video'
}

export interface ImageAssetItem extends BaseDisplayAssetItem {
  srcSet?: Array<{
    src: AssetSrcSet
    maxScreenWidth: number
    longEdge?: number
  }>
  zoomSrc?: string
  zoomSrcSet?: AssetSrcSet
  type: 'image'
}

export interface AudioAssetItem {
  getUrl: (fileType: string) => string
  type: 'audio'
}

export type DisplayAssetItem = VideoAssetItem | ImageAssetItem
